/* Tiny DOM helpers — no framework needed for a game this size. */

type Attrs = Record<string, string | number | boolean | undefined>;
type Child = Node | string | null | undefined | false;

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs | null = null,
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      if (value === undefined || value === false) continue;
      if (key === 'class') el.className = String(value);
      else if (key === 'text') el.textContent = String(value);
      else if (key === 'html') el.innerHTML = String(value);
      else if (value === true) el.setAttribute(key, '');
      else el.setAttribute(key, String(value));
    }
  }
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    el.append(child);
  }
  return el;
}

/**
 * Fire on pointer *down* for snappy game controls, while still supporting
 * keyboard activation (Enter/Space produce a click with detail === 0).
 */
export function onPress(el: HTMLElement, handler: (e: Event) => void): void {
  el.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    handler(e);
  });
  el.addEventListener('click', (e) => {
    if (e.detail === 0) handler(e);
  });
}

/** Tap to act, press-and-hold to do something else (e.g. toss food). */
export function onTapOrHold(
  el: HTMLElement,
  onTap: () => void,
  onHold: () => void,
  holdMs = 450,
): void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let held = false;
  const clear = (): void => {
    if (timer) clearTimeout(timer);
    timer = null;
    el.classList.remove('is-holding');
  };
  el.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    held = false;
    el.classList.add('is-holding');
    timer = setTimeout(() => {
      held = true;
      timer = null;
      el.classList.remove('is-holding');
      onHold();
    }, holdMs);
  });
  el.addEventListener('pointerup', () => {
    if (timer && !held) {
      clear();
      onTap();
    }
    clear();
  });
  el.addEventListener('pointerleave', clear);
  el.addEventListener('pointercancel', clear);
  el.addEventListener('contextmenu', (e) => e.preventDefault());
  el.addEventListener('click', (e) => {
    if (e.detail === 0) onTap();
  });
}

/** Drop null/false entries so optional children can be passed to replaceChildren(). */
export function kids(...children: Child[]): (Node | string)[] {
  return children.filter((c): c is Node | string => c !== null && c !== undefined && c !== false);
}

export function setText(el: HTMLElement, text: string): void {
  if (el.textContent !== text) el.textContent = text;
}

export function toggleClass(el: Element, name: string, on: boolean): void {
  if (el.classList.contains(name) !== on) el.classList.toggle(name, on);
}

export function setAttr(el: Element, name: string, value: string | null): void {
  if (value === null) {
    if (el.hasAttribute(name)) el.removeAttribute(name);
  } else if (el.getAttribute(name) !== value) {
    el.setAttribute(name, value);
  }
}

export function clear(el: Element): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}
